import React, {useEffect, useState} from "react";
import {Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography} from "@mui/material";
import {HighlightOff} from "@mui/icons-material";
import { Alert } from '@mui/material';
import {Field, Form} from "react-final-form";
import {calculator} from "../../reports/scorecards";
import {FormHelperEnhancedSelectWrapper} from "../../../components/EnhancedSelect";
import {FormHelperEnhancedTextWrapper} from "../../../components/EnhancedTextField";
import {FormHelperEnhancedRte} from "../../../components/EnhancedRte";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import {utilCreatePartnerSuspension, utilGetPartnerSuspensions} from "../../../utils/utils";
import ContentLoader from "react-content-loader";
import moment from "moment";
import { DialogHeader } from "../../../theme";

interface PartnerPauseDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    partnerIds: (number | string)[];
    onPause?: () => void;
}

export const PartnerPauseDialog = (props: PartnerPauseDialogProps) => {
    const {open, setOpen, partnerIds, onPause} = props;
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState({open: false, errorHeader: "", errorMessage: ""});
    const [initialValues, setInitialValues] = React.useState<any>(null);

    const checkValues = (values: any) => {
        // Note: `!x?.y` always yields a boolean, so the historical `?? true` on each of
        // these checks was already dead code — this is behaviorally identical to the
        // pre-migration JS, just without the unreachable right-hand operand that TS now
        // flags as a compile error (TS2869).
        if (!values?.reason) {
            return true;
        }
        if (!values?.start) {
            return true;
        }
        if (!values?.end) {
            return true;
        }
        if (!values?.message) {
            return true;
        }
        if (values.start && values.end && values.start > values.end) {
            return true;
        }

        return false;
    }

    const submit = async (values: any) => {
        const errors: string[] = [];
        setSubmitting(true);

        // Convert times to UTC
        values.start = moment(values.start).utc().format();
        values.end = moment(values.end).utc().format();

        const requests = partnerIds.map(async partnerId => {
            const response: any = await utilCreatePartnerSuspension(partnerId, values.reason, values.message, values.start, values.end);
            // utilCreatePartnerSuspension resolves to `undefined` on success and to
            // `{open, errorHeader, errorMessage}` on failure — it has no `.error` field.
            // Discriminate on `open` (always true on the failure object): `errorMessage` can be
            // `undefined` when the API error carries no `.data.Message`, so keying off it would
            // read a FAILED write as success and close the dialog. (Reading `.error` off the
            // `undefined` success value was the original LB-298 migration crash.)
            if (response?.open) {
                errors.push(response.errorMessage || response.errorHeader || "Error creating partner suspension.");
            }
            return response;
        });

        await Promise.all(requests);
        if (errors.length > 0) {
            setError({open: true, errorHeader: "Error", errorMessage: errors.join(", ")});
        }
        else {
            await new Promise(r => setTimeout(r, 750));
            setOpen(false);
            if(onPause) onPause();
        }
        setSubmitting(false);
    }

    useEffect(() => {
        const getExistingSuspension = async () => {
            const response = await utilGetPartnerSuspensions();
            // Success resolves to an array (possibly empty); failure resolves to
            // {open, errorHeader, errorMessage}. Discriminate on `open` (errorMessage can be
            // undefined when the API error has no Message) and only call `.find` on an actual
            // array, so an error object never reaches `.find` and hangs the loader.
            if (response?.open) {
                setError({open: true, errorHeader: "Error", errorMessage: response.errorMessage || "Error getting partner suspensions."});
            } else {
                const foundPartnerSuspension = Array.isArray(response)
                    // eslint-disable-next-line eqeqeq
                    ? response.find((suspension: any) => suspension.PartnerId == partnerIds[0])
                    : undefined;

                if(foundPartnerSuspension) {
                    // Convert times to local
                    foundPartnerSuspension.StartDate = moment(foundPartnerSuspension.StartDate).local().format("YYYY-MM-DDTHH:mm:ss");
                    foundPartnerSuspension.EndDate = moment(foundPartnerSuspension.EndDate).local().format("YYYY-MM-DDTHH:mm:ss");
                    
                    setInitialValues({
                        reason: foundPartnerSuspension.ReasonCode,
                        start: foundPartnerSuspension.StartDate,
                        end: foundPartnerSuspension.EndDate,
                        message: foundPartnerSuspension.Message
                    });
                }
                else {
                    setInitialValues(null);
                }
            }
        }

        const init = async () => {
            setLoading(true);
            await getExistingSuspension();
            setLoading(false);
        }

        if(partnerIds.length === 1)
            init();
        else {
            setInitialValues(null);
            setLoading(false);
        }

        // eslint-disable-next-line
    }, [open]);

    return (
        <>
            <Dialog open={open} maxWidth={"md"} fullWidth={true}>
                <DialogTitle style={{paddingBottom: "0px"}}>
                    <Grid container justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item container style={{flex: 1, flexDirection: "column"}}>
                            <DialogHeader>
                                Pause
                            </DialogHeader>
                        </Grid>

                        <IconButton onClick={() => setOpen(false)} size="large">
                            <HighlightOff style={{color: "red"}}/>
                        </IconButton>
                    </Grid>
                </DialogTitle>

                <DialogContent style={{paddingTop: "0", paddingBottom: "0"}}>
                    <Alert severity="info" style={{marginBottom: '1em'}}>
                        <Typography>
                            Pausing {partnerIds.length > 1 ? "these" : "this"} {partnerIds.length > 1 ? "suppliers" : "supplier"} will zero out their inventory until they are unpaused.
                        </Typography>
                    </Alert>

                    {initialValues !== null && (
                        <Alert severity="info" style={{marginBottom: '1em'}}>
                            <Typography>
                                There is a scheduled pause already for this partner and that information is pre-filled should you want to overwrite it.
                            </Typography>
                        </Alert>
                    )}

                    {loading ? (
                        <ContentLoader preserveAspectRatio='none' style={{height: '10em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                    ) : (
                        <Form onSubmit={submit} initialValues={initialValues} decorators={[calculator]}
                              render={({handleSubmit, pristine, invalid, values}) => {
                                  return (
                                      <form onSubmit={handleSubmit} style={{display: 'flex', width: '100%', gap: "0.5em"}}>
                                          <Grid container>
                                              <div style={{display: "flex", gap: "0.5em", width: "100%"}}>
                                                  <div style={{display: "flex", flexDirection: "column", flex: 1, gap: "0.5em"}}>
                                                      <Field
                                                          component={FormHelperEnhancedSelectWrapper as any}
                                                          name='reason'
                                                          label={"Reason for pausing"}
                                                          options={[
                                                              {label: "Business Closure", value: "Business Closure"},
                                                              {label: "Holiday/Seasonal", value: "Holiday/Seasonal"},
                                                              {label: "SLA Compliance", value: "SLA Compliance"},
                                                              {label: "Stale Inventory", value: "Stale Inventory"},
                                                              {label: "Other", value: "Other"}
                                                          ]}
                                                          formControlProps={{style: {flex: 1}}}
                                                          required
                                                      />
                                                      <Field component={FormHelperEnhancedTextWrapper} type='datetime-local'
                                                             label='Suspension start date' name='start' required
                                                      />
                                                      <Field component={FormHelperEnhancedTextWrapper} type='datetime-local'
                                                             label='Suspension end date' name='end' required
                                                             min={values["start"]}
                                                      />

                                                      {/*Allow Appeal Checkbox*/}
                                                      {/*<Field component={FormHelperEnhancedCheckbox} type='date' label='Allow Appeal' name='allowAppeal' />*/}
                                                  </div>

                                                  <div style={{flex: 1, height: "100%"}}>
                                                      {/*Message Box*/}
                                                      <Field component={FormHelperEnhancedRte} label='Message' formControlProps={{style: {height: "100%"}}}
                                                             name='message' required/>
                                                  </div>
                                              </div>
                                              <Grid container justifyContent={"flex-end"} style={{margin: "1em 0", width: "100%"}}>
                                                  <Button variant='text' type='submit'
                                                          disabled={pristine || checkValues(values)} color='primary'>
                                                      Submit
                                                  </Button>
                                              </Grid>
                                          </Grid>
                                      </form>
                                  );
                              }}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={submitting} onClose={(event: any, reason: string) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            }}>
                <DialogTitle>
                    <DialogHeader>
                        Submitting Pause Request
                    </DialogHeader>
                </DialogTitle>
                <DialogContent>
                    <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                        <CircularProgress/>
                    </Grid>
                </DialogContent>
            </Dialog>
            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </>
    );
}
